import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Text } from '@asigna/kit';
import { getAllMyMultisigs, getMyMultisignClaimById, getPendingMultisigs } from 'api';
import { AddContact, OwnerCard, SkeletonContact } from 'components';
import { useActivateSafeFromUrlParam } from 'hooks';
import useAddress from 'hooks/use-address';
import useNetwork from 'hooks/use-network';
import useData from 'hooks/useData';
import { useAtom } from 'jotai/index';
import { contactsAtom, usePendingSafes, useSafes } from 'store';
import { IClaimMultiSignWallet, IMultiSignWallet, StxChain, StxChainEnum } from 'types';
import { isDeepEqual } from 'utils/autoUpdateTools';

export const PendingSafeDetailsPage = () => {
  useActivateSafeFromUrlParam();
  const params = useParams<{ safeId: string }>();
  const safeId = params.safeId;
  const { setSafes } = useSafes();
  const { setPendingSafes } = usePendingSafes();
  const walletAccount = useAddress() || '';
  const [network] = useNetwork();
  const history = useHistory();
  const [isLoading, setLoading] = useState(false);
  const [pendingCurrentSafe, setPendingCurrentSafe] = useState<IClaimMultiSignWallet>();
  const [addToContactsDialogOpened, setAddToContactsDialogOpened] = useState(false);
  const [addingToContactsOwnerAddress, setAddingToContactsOwnerAddress] = useState('');
  const [contacts] = useAtom(contactsAtom);

  const { getContacts } = useData();

  const getData = async () => {
    try {
      const response = await getMyMultisignClaimById(safeId);
      if (response.data) {
        if (response.data.status === 'SUBMITTED') {
          history.push(`/vault/${response.data.address}/dashboard`);

          return;
        }
        if (!isDeepEqual(response.data, pendingCurrentSafe)) {
          setPendingCurrentSafe(response.data);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleInviteAccepted = async (safe?: IMultiSignWallet) => {
    const net = StxChainEnum[network] as any as StxChain;
    await getData();
    if (safe) {
      await Promise.all([getPendingMultisigs(net), getAllMyMultisigs(net)]).then(
        ([pendingMulitisgsResponse, multisigsResponse]) => {
          setPendingSafes(pendingMulitisgsResponse);
          setSafes(multisigsResponse);
          history.push(`/vault/${safe.address}/dashboard`);
        }
      );
    } else {
      await getPendingMultisigs(net).then(setPendingSafes);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      getData();
    }, 3000);

    return () => {
      clearInterval(timer);
    };
  }, [pendingCurrentSafe]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await getData();
      setLoading(false);
    };
    fetchData();
  }, []);

  const canJoin = useMemo(() => {
    return pendingCurrentSafe?.owners.find(
      (owner) => owner.address === walletAccount && !owner.verified
    );
  }, [pendingCurrentSafe?.owners]);

  return (
    <div className="flex w-full pr-0 sm:pr-24 pl-0 sm:pl-0 h-screen py-16">
      <div className="flex flex-col sm:rounded-[16px] sm:bg-black-1 w-full relative p-16 sm:p-32 overflow-y-scroll">
        <Text message="Owners" color="white" size="r24" className="mb-20" />
        <div className="flex flex-col gap-24">
          <div className="flex flex-col gap-16">
            <div className="flex flex-col gap-4">
              <Text message="Confirmation threshold" size="r14" color="regular" />
              {pendingCurrentSafe && (
                <Text
                  message={`${pendingCurrentSafe.threshold} out of ${pendingCurrentSafe.owners.length}`}
                  size="m14"
                  color="white"
                />
              )}
            </div>
            {isLoading && <SkeletonContact />}
            <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-16 lg:gap-24 2xl:gap-32 flex-wrap">
              {!isLoading &&
                pendingCurrentSafe &&
                pendingCurrentSafe.owners.map((item, index: number) => {
                  let name =
                    contacts?.find((x) => x.contact_address === item.address)?.contact_name ||
                    'Unknown';
                  if (walletAccount === item.address) name = 'Me';

                  return (
                    <OwnerCard
                      key={item.address}
                      name={name}
                      address={item.address}
                      verified={item.verified}
                      addressWallet={walletAccount}
                      safeId={safeId}
                      network={network}
                      onAddContact={() => {
                        setAddToContactsDialogOpened(true);
                        setAddingToContactsOwnerAddress(item.address);
                      }}
                      canJoin={Boolean(canJoin)}
                      onInviteAccepted={handleInviteAccepted}
                      index={index}
                    />
                  );
                })}
            </div>
          </div>
        </div>
        {addToContactsDialogOpened && (
          <AddContact
            open={addToContactsDialogOpened}
            onClose={() => setAddToContactsDialogOpened(false)}
            editingContact={null}
            presetWallet={addingToContactsOwnerAddress}
            fetchContacts={getContacts}
          />
        )}
      </div>
    </div>
  );
};
